// ./src/utils/browserHelpers.ts
import { UAParser } from "ua-parser-js";

// Parse user agent string
export const parseUserAgent = (
  userAgent: string | null | undefined,
): { browser: string; os: string; device: string } => {
  if (!userAgent) {
    return { browser: "Unknown", os: "Unknown", device: "Unknown" };
  }

  try {
    const parser = new UAParser(userAgent);
    const result = parser.getResult();

    const browser = result.browser.name
      ? `${result.browser.name} ${result.browser.version || ""}`
      : "Unknown";

    const os = result.os.name
      ? `${result.os.name} ${result.os.version || ""}`
      : "Unknown";

    const device =
      result.device.vendor || result.device.model
        ? `${result.device.vendor || ""} ${result.device.model || ""}`.trim()
        : result.device.type || "Desktop";

    return { browser, os, device };
  } catch {
    return { browser: "Unknown", os: "Unknown", device: "Unknown" };
  }
};

/**
 * Reload the app onto the latest deployed version.
 *
 * A plain `location.reload()` is not enough when a PWA service worker is
 * installed: the service worker serves the previously cached bundle, so the
 * screen would just reload onto the SAME (old) version. This is exactly what
 * happens when a "reload" command is sent to a TV after a new deploy.
 *
 * To guarantee the freshly deployed version loads, we first drop the service
 * worker and its caches, then reload — so the navigation and assets are fetched
 * from the network. The service worker re-registers itself on the next load.
 */
export const forceReloadToLatestVersion = async (): Promise<void> => {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }

    if (typeof caches !== "undefined") {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
  } catch {
    // Best-effort cleanup — reload regardless so the command is never a no-op.
  } finally {
    window.location.reload();
  }
};
